<!--
  DecorationLibraryArtworkDetail.vue -- U4 two-column governance layout.

  Handoff: planning/design-handoffs/handoff_2026-08-02/
           2026-08-02-decoration-library-detail/
  ADR 0063 -- Artwork detail: two-column governance layout, shipped
  scope. Locked Decisions 13-19 (design/Locked Decisions.md). Stacks on
  feat/decolib-redesign (U1 luminance backdrop, U2 richer library data +
  where_used endpoint).

  URL: /decoration-library/artwork/:artwork_id (artwork-rooted per
  the P5 UI design decisions doc -- locked 2026-06-23 by Jake).

  Layout (ADR 0063)
  ------------------
  Left rail (sticky >= 1040px): artwork preview (272px, contain-fit,
  shared DecorationArtThumb backdrop) + a session-only switcher (Auto/Light/
  Dark/Checker) + the file record (format, pixel dims, resolution,
  color count, file size, uploaded by, uploaded date -- computed
  server-side, see src/services/artwork_file_metadata.py +
  artwork_file_metadata_service.py on the backend). A stitch file
  (.dst/.emb) shows Design size (inches + mm) and Stitch count from the
  preview drain's parse instead of pixel dims and resolution. A
  genuinely-null field renders "Unknown" (or "Vector" for a vector
  format's resolution) rather than fabricating data.

  Right working column, in order: header (name, status chip, client,
  actions) -> Decorations (one card per decoration; no method tabs) ->
  Where it's used (grouped by store, via the U2
  GET /api/artworks/<id>/where_used endpoint) -> Manage (Archive always
  available, Delete gated on product_count == 0).

  Below 1040px the layout collapses to one column and the rail stops
  being sticky (see the scoped <style> media query).

  Explicitly removed (ADR 0063 / Locked Decision 20) and NOT stubbed:
  the "Approval matrix coming soon" placeholder, the v1 version chip,
  the per-client approval axis, and the italic "Digitization pending"
  band inside a card's artwork slot (replaced by the readiness chip in
  the card header).

  Data sources
  ------------
  * ``GET /api/artworks/<id>/decorations`` -- artwork header + method-
    grouped Decoration rows (unchanged endpoint; this page flattens the
    method groups into one card list instead of tabs).
  * ``GET /api/artworks/<id>/where_used`` -- U2 endpoint backing the
    "Where it's used" section (grouped-by-store shape + rollup).

  Preserved from prior PRs
  -------------------------
  * #2363: the artwork_preview sidecar consumption (pending/ready/
    failed tiles + poll) for .dst/.emb stitch files -- feeds the
    preview rail image.
  * The cascade-delete plumbing (confirm modal, delete-preview
    round-trip, 409 in-flight-orders handling) for both the artwork
    scope (now reached via Manage, gated on usage) and the per-
    decoration scope (unchanged, still on each card).

  Vocabulary
  ----------
  Per ``docs/integrations/decoration/vocabulary.md`` the surface noun
  stays "Decoration." The new shape: artwork + method + dimensions
  is each card; the binding to a product zone is a "Placement."
-->
<template>
    <v-container fluid class="dla-container" data-testid="decoration-library-artwork-detail-root">
        <div class="dla" data-testid="decoration-library-artwork-detail">
            <button
                type="button"
                class="dla__backlink"
                data-testid="dla-back-link"
                @click="goBack"
            >
                &larr; Decoration library
            </button>

            <!-- Page-level loading. -->
            <div
                v-if="loading"
                class="dla__loading"
                data-testid="dla-loading"
            >
                <v-progress-circular
                    indeterminate
                    color="primary"
                />
                <span>Loading artwork...</span>
            </div>

            <!-- Not found / closed-oracle 404. -->
            <div
                v-else-if="notFound"
                class="dla__not-found"
                data-testid="dla-not-found"
            >
                <div class="dla__not-found-title">
                    Artwork not found
                </div>
                <div class="dla__not-found-sub">
                    The artwork you requested does not exist or you do
                    not have access to it.
                </div>
                <button
                    type="button"
                    class="dla__not-found-back"
                    data-testid="dla-not-found-back"
                    @click="goBack"
                >
                    Back to library
                </button>
            </div>

            <!-- Generic load error. -->
            <div
                v-else-if="loadError"
                class="dla__error"
                data-testid="dla-error"
            >
                {{ loadError }}
            </div>

            <template v-else>
                <div class="dla__split">
                    <!-- Left rail: preview + backdrop switcher + file record. -->
                    <div
                        class="dla__rail"
                        data-testid="dla-rail"
                    >
                        <div class="dla__rail-card">
                            <div
                                class="dla__preview-frame"
                                data-testid="dla-preview-frame"
                                :data-backdrop="previewBackdropKey"
                                :style="{ background: previewBackdropColor }"
                            >
                                <div
                                    v-if="previewKind === 'pending'"
                                    class="dla__thumb-pending"
                                    data-testid="decoration-detail-preview-generating"
                                    role="status"
                                    aria-label="Generating preview"
                                >
                                    <v-progress-circular
                                        indeterminate
                                        size="20"
                                        width="2"
                                        color="primary"
                                    />
                                    <span class="dla__thumb-pending-text">
                                        Generating preview...
                                    </span>
                                </div>
                                <div
                                    v-else-if="previewKind === 'failed'"
                                    class="dla__thumb-failed"
                                    data-testid="decoration-detail-preview-unavailable"
                                >
                                    <span class="dla__thumb-failed-text">
                                        {{ previewFailedMessage }}
                                    </span>
                                </div>
                                <div
                                    v-else-if="previewKind === 'unsupported'"
                                    class="dla__thumb-no-preview"
                                    data-testid="decoration-detail-preview-no-preview"
                                >
                                    <span
                                        class="dla__thumb-no-preview-text"
                                        data-testid="decoration-detail-preview-no-preview-message"
                                    >{{ previewFailedMessage }}</span>
                                    <span
                                        class="dla__thumb-no-preview-hint"
                                        data-testid="decoration-detail-preview-no-preview-hint"
                                    >{{ previewHint }}</span>
                                </div>
                                <DecorationArtThumb
                                    v-else-if="thumbnailSrc"
                                    :src="thumbnailSrc"
                                    :ink-hex="colorwayPreview ? null : artwork && artwork.dominant_ink_hex"
                                    :ink-mixed="colorwayPreview ? false : !!(artwork && artwork.dominant_ink_mixed)"
                                    :backdrop="backdrop"
                                    :alt="displayName"
                                    data-testid="dla-header-thumb"
                                    :data-colorway-preview="
                                        colorwayPreview
                                            ? colorwayPreview.colorwayId
                                            : null
                                    "
                                    class="dla__preview-img"
                                    @resolved="autoBackdropKey = $event"
                                />
                                <div
                                    v-else
                                    class="dla__thumb-placeholder"
                                    data-testid="dla-header-thumb-placeholder"
                                >
                                    {{ initialOf(displayName) }}
                                </div>
                                <div
                                    v-if="colorwayPreviewBusy"
                                    class="dla__colorway-busy"
                                    data-testid="library-colorway-preview-busy"
                                    role="status"
                                    aria-label="Rendering colorway preview"
                                >
                                    <v-progress-circular
                                        indeterminate
                                        size="24"
                                        width="2"
                                        color="primary"
                                    />
                                </div>
                            </div>

                            <div class="dla__backdrop-row">
                                <span class="dla__backdrop-label">
                                    Preview backdrop
                                </span>
                                <div
                                    class="dla__backdrop-switcher"
                                    data-testid="dla-backdrop-switcher"
                                >
                                    <button
                                        v-for="opt in backdropOptions"
                                        :key="opt.key"
                                        type="button"
                                        class="dla-backdrop-option"
                                        :class="{
                                            'dla-backdrop-option--active':
                                                backdrop === opt.key,
                                        }"
                                        :data-testid="
                                            `dla-backdrop-option-${opt.key}`
                                        "
                                        :title="`${opt.label} backdrop`"
                                        @click="setBackdrop(opt.key)"
                                    >
                                        {{ opt.label }}
                                    </button>
                                </div>
                            </div>

                            <!--
                              Entrypoint to the thread-colorway editor
                              (card https://trello.com/c/2hcgDM7J). Without
                              this the editor route is unreachable from the
                              UI -- it shipped that way once, which is not
                              shipped at all.
                            -->
                            <div class="dla__colorways-row">
                                <router-link
                                    class="dla__colorways-link"
                                    data-testid="dla-colorways-link"
                                    :to="{
                                        name: 'library_artwork_colorways',
                                        params: {
                                            artwork_id:
                                                artwork_id ||
                                                $route.params.artwork_id,
                                        },
                                    }"
                                >
                                    <v-icon aria-hidden="true" size="small">
                                        palette
                                    </v-icon>
                                    Edit thread colorways
                                </router-link>
                                <!--
                                  VIEW-ONLY preview picker: changes the image
                                  on this page, never saved, binds nothing.
                                -->
                                <LibraryColorwayPreviewSelect
                                    class="dla__colorways-select"
                                    :artwork-id="artwork_id || $route.params.artwork_id"
                                    :filename="displayFilename"
                                    :disabled="previewKind !== 'ready' || !canWrite"
                                    :disabled-note="colorwayPreviewDisabledNote"
                                    @preview="colorwayPreview = $event"
                                    @busy="colorwayPreviewBusy = $event"
                                />
                            </div>

                            <div
                                class="dla__file-record"
                                data-testid="dla-file-record"
                            >
                                <div
                                    v-for="row in fileRecordRows"
                                    :key="row.key"
                                    class="dla__file-record-row"
                                    :data-testid="
                                        `dla-file-record-${row.key}`
                                    "
                                >
                                    <span class="dla__file-record-k">
                                        {{ row.label }}
                                    </span>
                                    <span class="dla__file-record-v">
                                        {{ row.value }}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Right working column. -->
                    <div
                        class="dla__working-column"
                        data-testid="dla-working-column"
                    >
                        <section
                            class="dla__header"
                            data-testid="dla-header"
                        >
                            <div class="dla__header-main">
                                <div class="dla__header-title-row">
                                    <h1
                                        class="dla__header-title"
                                        data-testid="dla-header-title"
                                    >
                                        {{ displayName }}
                                    </h1>
                                    <span
                                        class="dla__status-chip"
                                        :class="`dla__status-chip--${statusInfo.key}`"
                                        data-testid="dla-status-chip"
                                    >
                                        {{ statusInfo.label }}
                                    </span>
                                </div>
                                <div
                                    v-if="originalFileName"
                                    class="dla__header-file"
                                    data-testid="dla-original-filename"
                                >
                                    File: {{ originalFileName }}
                                </div>
                                <label
                                    v-if="canWrite"
                                    class="dla__name-field"
                                >
                                    <span class="dla__name-label">Name</span>
                                    <input
                                        v-model="nameDraft"
                                        type="text"
                                        class="dla__name-input"
                                        maxlength="200"
                                        autocomplete="off"
                                        :disabled="nameSaving"
                                        data-testid="dla-name-input"
                                        @change="saveName"
                                        @keydown.enter.prevent="$event.target.blur()"
                                    >
                                </label>
                                <p
                                    v-if="nameError"
                                    class="dla__header-error"
                                    role="alert"
                                    data-testid="dla-name-error"
                                >
                                    {{ nameError }}
                                </p>
                                <div
                                    v-if="clientName"
                                    class="dla__header-client"
                                    data-testid="dla-header-client"
                                >
                                    {{ clientName }}
                                </div>
                                <ArtworkIdentifiers
                                    v-if="artwork"
                                    scope="detail"
                                    :artwork-id="artwork.id"
                                    :design-code="artwork.design_code"
                                    :decorator-code="artwork.decorator_artwork_code"
                                    :facts="factItems"
                                />
                            </div>
                            <div class="dla__header-actions">
                                <button
                                    type="button"
                                    class="dla__btn dla__btn--primary"
                                    data-testid="dla-add-decoration-btn"
                                >
                                    Add decoration
                                </button>
                                <button
                                    v-if="canReplace"
                                    type="button"
                                    class="dla__btn"
                                    data-testid="dla-replace-artwork-btn"
                                    @click="replaceOpen = true"
                                >
                                    Replace artwork
                                </button>
                                <button
                                    type="button"
                                    class="dla__btn"
                                    data-testid="dla-download-btn"
                                    :data-state="downloadState"
                                    :disabled="downloadState === 'downloading'"
                                    @click="downloadArtwork"
                                >
                                    {{
                                        downloadState === 'downloading'
                                            ? 'Downloading...'
                                            : 'Download'
                                    }}
                                </button>
                            </div>
                            <p
                                v-if="downloadError"
                                class="dla__header-error"
                                role="alert"
                                data-testid="dla-download-error"
                            >
                                {{ downloadError }}
                            </p>
                        </section>

                        <ArtworkReplaceModal
                            v-if="replaceOpen && artwork"
                            :artwork="artwork"
                            :old-name="displayName"
                            @replaced="onArtworkReplaced"
                            @close="replaceOpen = false"
                        />

                        <!-- Decorations. -->
                        <section class="dla__section">
                            <div class="dla__section-head">
                                <h2 class="dla__section-title">
                                    Decorations
                                </h2>
                                <span class="dla__section-count">
                                    {{ allDecorationCards.length }} made
                                    from this artwork
                                </span>
                            </div>

                            <div
                                v-if="!allDecorationCards.length"
                                class="dla__empty"
                                data-testid="dla-empty"
                            >
                                <p class="dla__empty-text">
                                    No decorations yet. Add a placement
                                    to bind this artwork to a product
                                    zone.
                                </p>
                            </div>

                            <div v-else class="dla__cards">
                                <article
                                    v-for="card in allDecorationCards"
                                    :key="card.id"
                                    class="dla__card"
                                    :data-testid="`dla-card-${card.id}`"
                                >
                                    <div class="dla__card-head">
                                        <span class="dla__method-chip">
                                            {{ card.methodLabel }}
                                        </span>
                                        <span
                                            class="dla__card-dims"
                                            :data-testid="
                                                `dla-card-dims-${card.id}`
                                            "
                                        >
                                            {{ dimsLabel(card) }}
                                        </span>
                                    </div>

                                    <div class="dla__card-meta">
                                        <div class="dla__card-meta-col">
                                            <div class="dla__card-meta-k">
                                                Spec
                                            </div>
                                            <div class="dla__card-meta-v">
                                                {{ card.substrate_target }}
                                                <span
                                                    v-if="
                                                        card.light_dark_pair_id !=
                                                        null
                                                    "
                                                    :data-testid="
                                                        `dla-card-pair-${card.id}`
                                                    "
                                                >
                                                    &middot; Light/dark pair
                                                </span>
                                            </div>
                                            <div
                                                v-if="stitchLabel(card)"
                                                class="dla__card-meta-v dla__card-stitch"
                                                :data-testid="
                                                    `dla-card-stitch-${card.id}`
                                                "
                                            >
                                                {{ stitchLabel(card) }}
                                            </div>
                                        </div>
                                        <div class="dla__card-meta-col">
                                            <div class="dla__card-meta-k">
                                                On products
                                            </div>
                                            <div
                                                class="dla__card-meta-v"
                                                :data-testid="
                                                    `dla-card-placements-${card.id}`
                                                "
                                            >
                                                {{ placementsLabel(card) }}
                                            </div>
                                        </div>
                                    </div>

                                    <div
                                        v-if="canWrite"
                                        class="dla__card-actions"
                                    >
                                        <button
                                            type="button"
                                            class="dla__btn dla__btn--danger dla__btn--sm"
                                            :data-testid="
                                                `dla-delete-decoration-btn-${card.id}`
                                            "
                                            @click="
                                                openDecorationDeleteConfirm(card)
                                            "
                                        >
                                            Delete decoration
                                        </button>
                                    </div>
                                </article>
                            </div>
                        </section>

                        <!--
                            Digitization and approval: two separate
                            things, two cards (Jake, 2026-10-27).
                        -->
                        <DecorationDigitizationCard
                            :artwork-id="artwork.id"
                            :cards="allDecorationCards"
                            :capabilities="capabilities"
                            @changed="refreshDecorations"
                        />
                        <DecorationApprovalCard
                            :artwork-id="artwork.id"
                            :cards="allDecorationCards"
                            :can-override="canOverride"
                            :clients="overrideClients"
                            :artwork-client-ids="artwork.client_ids || []"
                            :breakdown="artwork.approval_breakdown || null"
                            @overridden="refreshDecorations"
                            @retracted="refreshDecorations"
                        />

                        <!-- Where it's used. -->
                        <section class="dla__section">
                            <div class="dla__section-head">
                                <h2 class="dla__section-title">
                                    Where it's used
                                </h2>
                            </div>

                            <div
                                v-if="whereUsedLoading"
                                class="dla__where-used-loading"
                                data-testid="dla-where-used-loading"
                            >
                                Loading usage...
                            </div>

                            <div
                                v-else-if="!whereUsed.groups.length"
                                class="dla__where-used-empty"
                                data-testid="dla-where-used-empty"
                            >
                                <p class="dla__where-used-empty-text">
                                    This artwork isn't bound to any
                                    products yet. Artwork is
                                    account-wide -- it becomes bound to
                                    a product from a store's Product
                                    editor, and stays available to every
                                    store on this account.
                                </p>
                                <router-link
                                    :to="{ name: 'distributor_stores' }"
                                    class="dla__btn"
                                    data-testid="dla-where-used-stores-link"
                                >
                                    Go to Stores
                                </router-link>
                            </div>

                            <div v-else class="dla__where-used">
                                <div
                                    class="dla__where-used-rollup"
                                    data-testid="dla-where-used-rollup"
                                >
                                    <strong>
                                        {{ whereUsed.rollup.product_count }}
                                        product{{
                                            whereUsed.rollup
                                                .product_count === 1
                                                ? ''
                                                : 's'
                                        }}
                                    </strong>
                                    across
                                    {{ whereUsed.rollup.store_count }}
                                    store{{
                                        whereUsed.rollup.store_count === 1
                                            ? ''
                                            : 's'
                                    }}
                                    &middot;
                                    {{ whereUsed.rollup.live_store_count }}
                                    live
                                </div>

                                <div
                                    v-for="group in whereUsed.groups"
                                    :key="group.store_id"
                                    class="dla__store-group"
                                    :data-testid="
                                        `dla-where-used-store-${group.store_id}`
                                    "
                                >
                                    <div class="dla__store-group-head">
                                        <router-link
                                            :to="storeEditRoute(group)"
                                            class="dla__store-group-name dla__store-group-name--link"
                                            :data-testid="
                                                `dla-whereused-store-link-${group.store_id}`
                                            "
                                        >
                                            {{
                                                group.store_name ||
                                                'Unnamed store'
                                            }}
                                        </router-link>
                                        <span
                                            class="dla__store-status-chip"
                                            :class="
                                                group.store_status ===
                                                'Live'
                                                    ? 'dla__store-status-chip--live'
                                                    : 'dla__store-status-chip--draft'
                                            "
                                        >
                                            {{ group.store_status }}
                                        </span>
                                    </div>
                                    <router-link
                                        v-for="product in group.products"
                                        :key="product.product_id"
                                        :to="productEditorRoute(product)"
                                        class="dla__store-product-row dla__store-product-row--link"
                                        :data-testid="
                                            `dla-whereused-product-link-${product.product_id}`
                                        "
                                    >
                                        <ProductThumbHoverZoom
                                            :image-url="product.thumbnail_url"
                                            :alt="
                                                product.product_name ||
                                                'Product image'
                                            "
                                            testid-base="dla-where-used-thumb"
                                            :id="product.product_id"
                                        />
                                        <span
                                            class="dla__store-product-name"
                                        >
                                            {{
                                                product.product_name ||
                                                'Unnamed product'
                                            }}
                                        </span>
                                        <span
                                            class="dla__store-product-zone"
                                        >
                                            {{
                                                product.placement_zone ||
                                                'Unspecified zone'
                                            }}
                                        </span>
                                        <span
                                            class="dla__store-product-method"
                                        >
                                            {{ product.method }}
                                        </span>
                                        <!-- Size of THIS usage. One row
                                             per placement, so the same
                                             artwork can appear on the
                                             same product at several
                                             sizes; without this the rows
                                             are indistinguishable
                                             (Jake, 8089, 2026-08-23). -->
                                        <span
                                            class="dla__store-product-dims"
                                            :data-testid="
                                                `dla-whereused-dims-${product.placement_id}`
                                            "
                                        >
                                            {{ usageDimsLabel(product) }}
                                        </span>
                                        <!-- The SAME size, under a second,
                                             placement-id-independent anchor,
                                             on the first usage row only. A
                                             ui-runner scenario cannot name
                                             the dynamic testid above (see
                                             firstUsagePlacementId). Rendered
                                             as its own element rather than a
                                             second attribute because an
                                             element carries one data-testid;
                                             aria-hidden keeps the duplicate
                                             copy away from assistive tech.

                                             NAMED `dla-whereused-first-dims`
                                             and deliberately NOT
                                             `dla-whereused-dims-first`: the
                                             latter shares the
                                             `dla-whereused-dims-` prefix that
                                             whereused-dims.spec.js queries
                                             with `^=` to count the usage
                                             rows, so it was counted as a
                                             third row and reddened that test.
                                             Keep any future anchor outside
                                             that prefix. -->
                                        <span
                                            v-if="
                                                product.placement_id ===
                                                firstUsagePlacementId
                                            "
                                            class="dla__store-product-dims dla__store-product-dims--anchor"
                                            data-testid="dla-whereused-first-dims"
                                            aria-hidden="true"
                                        >
                                            {{ usageDimsLabel(product) }}
                                        </span>
                                    </router-link>
                                </div>
                            </div>
                        </section>

                        <!-- Manage. -->
                        <section class="dla__section">
                            <div class="dla__section-head">
                                <h2 class="dla__section-title">
                                    Manage
                                </h2>
                            </div>
                            <div class="dla__manage-row">
                                <div class="dla__manage-copy">
                                    <div class="dla__manage-title">
                                        Archive or delete this artwork
                                    </div>
                                    <div
                                        class="dla__manage-reason"
                                        data-testid="dla-manage-reason"
                                    >
                                        {{ manageReasonText }}
                                    </div>
                                    <div
                                        v-if="archiveError"
                                        class="dla__manage-error"
                                        data-testid="dla-manage-archive-error"
                                    >
                                        {{ archiveError }}
                                    </div>
                                </div>
                                <template v-if="canWrite">
                                    <button
                                        type="button"
                                        class="dla__btn"
                                        data-testid="dla-manage-archive"
                                        :disabled="archiveSubmitting"
                                        @click="archiveArtwork"
                                    >
                                        {{
                                            archiveSubmitting
                                                ? 'Archiving...'
                                                : 'Archive'
                                        }}
                                    </button>
                                    <button
                                        type="button"
                                        class="dla__btn dla__btn--danger"
                                        data-testid="dla-manage-delete"
                                        :disabled="!canDeleteArtwork"
                                        @click="openArtworkDeleteConfirm"
                                    >
                                        Delete
                                    </button>
                                </template>
                            </div>
                        </section>
                    </div>
                </div>
            </template>

            <!--
                Shared delete-confirm modal. Used for both artwork
                delete (Manage section) and per-decoration delete
                (card button). The ``deleteScope`` data field
                discriminates so the confirm action hits the right
                cascade endpoint.
            -->
            <div
                v-if="deleteScope"
                class="dla__modal-backdrop"
                data-testid="dla-delete-modal"
                @click.self="closeDeleteConfirm"
            >
                <div
                    class="dla__modal"
                    role="dialog"
                    aria-labelledby="dla-delete-title"
                >
                    <h3
                        id="dla-delete-title"
                        class="dla__modal-title"
                    >
                        {{ deleteModalTitle }}
                    </h3>
                    <p
                        class="dla__modal-body"
                        data-testid="dla-delete-modal-body"
                    >
                        <span v-if="deletePreviewLoading">
                            Checking cascade footprint...
                        </span>
                        <span v-else-if="deleteScope === 'artwork'">
                            Delete artwork
                            &ldquo;{{ displayName }}&rdquo;? This
                            will remove
                            <strong
                                data-testid="dla-delete-modal-decorations"
                            >
                                {{ deletePreviewSummary.decorations }}
                                Decoration{{
                                    deletePreviewSummary.decorations === 1
                                        ? ''
                                        : 's'
                                }}
                            </strong>
                            and
                            <strong
                                data-testid="dla-delete-modal-placements"
                            >
                                {{ deletePreviewSummary.placements }}
                                Placement{{
                                    deletePreviewSummary.placements === 1
                                        ? ''
                                        : 's'
                                }}
                            </strong>
                            across
                            <strong
                                data-testid="dla-delete-modal-products"
                            >
                                {{
                                    deletePreviewSummary.campaign_products
                                }}
                                product{{
                                    deletePreviewSummary.campaign_products
                                        === 1
                                        ? ''
                                        : 's'
                                }}.
                            </strong>
                        </span>
                        <span v-else>
                            Remove this Decoration? The
                            <strong
                                data-testid="dla-delete-modal-placements"
                            >
                                {{
                                    decorationDeleteCandidate
                                        ? decorationDeleteCandidate
                                              .placements_count || 0
                                        : 0
                                }}
                                Placement{{
                                    decorationDeleteCandidate &&
                                    decorationDeleteCandidate
                                        .placements_count === 1
                                        ? ''
                                        : 's'
                                }}
                            </strong>
                            bound to it will be unbound.
                        </span>
                    </p>
                    <p
                        v-if="deletePreviewSummary.in_flight_orders > 0"
                        class="dla__modal-warn"
                        data-testid="dla-delete-modal-inflight-warn"
                    >
                        Heads up:
                        {{ deletePreviewSummary.in_flight_orders }} in-flight
                        order(s) reference this artwork's placements. The
                        server will block the delete -- wait for orders to
                        ship or cancel them first.
                    </p>
                    <p
                        v-if="deleteError"
                        class="dla__modal-error"
                        data-testid="dla-delete-modal-error"
                    >
                        {{ deleteError }}
                    </p>
                    <div class="dla__modal-actions">
                        <button
                            type="button"
                            class="dla__btn"
                            data-testid="dla-delete-modal-cancel"
                            @click="closeDeleteConfirm"
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            class="dla__btn dla__btn--danger"
                            data-testid="dla-delete-modal-confirm"
                            :disabled="deleteSubmitting"
                            @click="confirmDelete"
                        >
                            {{ deleteSubmitting ? 'Deleting...' : 'Delete' }}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </v-container>
</template>

<script>
import axios from '@/axios'
import { formatArtworkName } from '@/utils/formatArtworkName'
import { artworkDisplayName } from '@/utils/artworkDisplay'
import { parseDispositionFilename } from '@/utils/parseDispositionFilename'
import {
    artworkPreviewDisplay,
    artworkThumbnailSrc,
    isNonTerminalPreview,
} from '@/utils/artworkPreviewState'
import ProductThumbHoverZoom from '@/components/common/ProductThumbHoverZoom.vue'
import DecorationArtThumb from '@/components/decoration/DecorationArtThumb.vue'
import ArtworkIdentifiers from '../ArtworkIdentifiers.vue'
import { artworkFactItems } from '../artworkFacts'
import DecorationApprovalCard from './DecorationApprovalCard.vue'
import DecorationDigitizationCard from './DecorationDigitizationCard.vue'
import { useClientsStore } from '@/stores/clients'
import ArtworkReplaceModal from './ArtworkReplaceModal.vue'
import LibraryColorwayPreviewSelect from './LibraryColorwayPreviewSelect.vue'
import { ART_BACKDROP_CSS } from '@/utils/artBackdrop'
import {
    ARTWORK_FILE_KIND,
    artworkFileKind,
} from '@/utils/artworkUploadContract'

// Client lazy-render poll interval for the artwork_preview sidecar.
// Mirrors DecorationLibraryList.vue's PREVIEW_POLL_INTERVAL_MS -- the
// server-side ensure_preview trigger fires on every GET of the
// decorations endpoint (same as the library endpoint), so the client
// just re-polls that same endpoint until the sidecar reaches a
// terminal state.
const PREVIEW_POLL_INTERVAL_MS = 2500

// ADR 0063 Locked Decision 3: backdrop switcher options. Session state
// only -- never persisted to the artwork.
const BACKDROP_OPTIONS = [
    { key: 'auto', label: 'Auto' },
    { key: 'light', label: 'Light' },
    { key: 'dark', label: 'Dark' },
    { key: 'checker', label: 'Checker' },
]

// Library/usage status vocabulary (ADR 0042 / ADR 0062). Only this
// axis is shown -- the per-client approval axis is unbuilt and is
// represented nowhere on this page.
const STATUS_LABELS = {
    draft: 'Draft',
    ready: 'Ready',
    'in-use': 'In use',
    archived: 'Archived',
}

export default {
    name: 'DecorationLibraryArtworkDetail',
    components: {
        ArtworkIdentifiers,
        ArtworkReplaceModal,
        DecorationArtThumb,
        DecorationApprovalCard,
        DecorationDigitizationCard,
        LibraryColorwayPreviewSelect,
        ProductThumbHoverZoom,
    },
    props: {
        artwork_id: {
            type: [String, Number],
            default: null,
        },
    },
    inject: {
        distributorLayoutCapabilities: {
            default: () => [],
        },
    },
    data() {
        return {
            artwork: null,
            methods: [],
            // GET /api/clients/ rows for the size cards' "Approve on
            // behalf of a client" picker; read only for an override holder.
            clientRows: [],
            loading: true,
            loadError: null,
            notFound: false,
            // Editable artwork name (#703): the field's text, whether a
            // save is in flight, and the last refusal.
            nameDraft: '',
            nameSaving: false,
            nameError: null,
            // artwork_preview sidecar poll (mirrors DecorationLibraryList.vue
            // PR-6). Non-null only while the sidecar is non-terminal;
            // cleared on reaching a terminal state and on unmount.
            previewPollTimer: null,
            previewPollInFlight: false,
            // ADR 0063 Locked Decision 3: session-only preview aid, never
            // sent to the server or stored on the artwork.
            backdrop: 'auto',
            // The key DecorationArtThumb resolved for Auto (see
            // previewBackdropKey).
            autoBackdropKey: 'light',
            // U2 "Where it's used" (ADR 0063 Locked Decision 16).
            whereUsed: {
                groups: [],
                rollup: {
                    product_count: 0,
                    store_count: 0,
                    live_store_count: 0,
                },
            },
            whereUsedLoading: true,
            // ``deleteScope`` is null when the modal is closed;
            // either 'artwork' or 'decoration' when open. The two
            // scopes share the same modal, error surfaces, and
            // preview-summary state to keep the markup simple.
            deleteScope: null,
            decorationDeleteCandidate: null,
            deletePreviewLoading: false,
            deletePreviewSummary: {
                decorations: 0,
                placements: 0,
                campaign_products: 0,
                in_flight_orders: 0,
            },
            deleteSubmitting: false,
            deleteError: null,
            // Manage -- Archive (ADR 0063 Locked Decision 18).
            archiveSubmitting: false,
            archiveError: null,
            // Header Download (Bug Intake #297): 'idle' | 'downloading' |
            // 'done' | 'error'. Rendered as data-state for ui-runner.
            downloadState: 'idle',
            downloadError: null,
            // Header Replace artwork (Bug Intake #297).
            replaceOpen: false,
            // "Preview colorway" picker: { url, colorwayId } while a
            // colorway is being previewed, else null (the default preview).
            // VIEW-ONLY session state -- never persisted, never sent.
            colorwayPreview: null,
            colorwayPreviewBusy: false,
        }
    },
    computed: {
        routeArtworkId() {
            // Prefer the prop (router passes via ``props: true``) and
            // fall back to ``$route.params`` so the component remains
            // mountable in a test harness that doesn't drive the
            // router.
            return (
                this.artwork_id ??
                this.$route?.params?.artwork_id ??
                null
            )
        },
        colorwayPreviewDisabledNote() {
            if (!this.canWrite) {
                return 'You need permission to edit decorations to preview colorways.'
            }
            if (this.previewKind !== 'ready') {
                return 'Available once the stitch preview has finished generating.'
            }
            return ''
        },
        displayFilename() {
            const a = this.artwork
            return (a && (a.original_filename || a.filename)) || ''
        },
        displayName() {
            if (!this.artwork) return ''
            // The editable NAME (artworks.filename, '#<guid>' suffix
            // stripped), not the raw uploaded file name (#703); the real
            // file name is shown separately by originalFileName.
            return artworkDisplayName(this.artwork)
        },
        // The uploaded file's real name: read-only, never editable.
        originalFileName() {
            const a = this.artwork
            return (a && formatArtworkName(a.original_filename)) || ''
        },
        clientName() {
            return (this.artwork && this.artwork.client && this.artwork.client.name) || null
        },
        previewDisplayInfo() {
            // Map the artwork to a display decision via the shared helper --
            // the SAME mapping the Decoration Library list uses
            // (DecorationLibraryList.vue thumbnailSrc / previewKind): the
            // "No preview for this file type" state first, else the
            // artwork_preview sidecar (PR-4). null preview (raster/SVG) ->
            // kind 'none'.
            return artworkPreviewDisplay(this.artwork)
        },
        previewKind() {
            return this.previewDisplayInfo.kind
        },
        previewFailedMessage() {
            return this.previewDisplayInfo.message || ''
        },
        previewHint() {
            return this.previewDisplayInfo.hint || ''
        },
        thumbnailSrc() {
            // A READY stitch-file (.dst/.emb) sidecar renders its cached
            // render (preview_url) as the header image. Non-null preview in
            // any other state is handled by the pending/failed tiles in the
            // template, so this only returns the preview_url for the ready
            // case.
            if (!this.artwork) return null
            if (this.previewDisplayInfo.kind === 'ready') {
                // A previewed colorway (view-only) replaces the default
                // render; null falls through to the original.
                if (this.colorwayPreview) return this.colorwayPreview.url
                return this.previewDisplayInfo.previewUrl
            }
            // Raster/SVG (preview null): prefer the server-generated
            // thumbnail; fall back to the full image_url. thumbnail_url is
            // NULL until process_assets runs (e.g. seeded artworks), but
            // image_url is set at upload/seed time -- so the fallback
            // renders the real image instead of the letter placeholder. A
            // raw EPS/AI/PDF is never drawn (a broken image): it shows only
            // via its PNG stand-in. Mirrors DecorationLibraryList.vue.
            return artworkThumbnailSrc(this.artwork)
        },
        backdropOptions() {
            return BACKDROP_OPTIONS
        },
        previewBackdropKey() {
            // ADR 0063 Locked Decision 3: Auto is the shared
            // DecorationArtThumb decision (sampled ink -> WCAG contrast),
            // reported back via its ``resolved`` event so the frame's
            // padding matches the art; the other three options are a
            // manual override for this session only. Before an image
            // renders (pending / failed / placeholder) Auto is light.
            if (this.backdrop !== 'auto') return this.backdrop
            return this.thumbnailSrc ? this.autoBackdropKey : 'light'
        },
        previewBackdropColor() {
            return ART_BACKDROP_CSS[this.previewBackdropKey]
        },
        factItems() {
            // Header facts line: stitches, colors, added by/when, digitizer
            // (artworkFacts.js). The method slugs gate stitches/colors.
            const slugs = (this.methods || []).map(
                (g) => g && g.method && g.method.slug,
            )
            // Approval is on the Approval card below, not repeated here.
            return artworkFactItems(this.artwork, slugs, { approval: false })
        },
        fileRecordRows() {
            // ADR 0063 Locked Decision 14: file metadata is first-class.
            // The backend header payload carries pixel_width/
            // pixel_height/color_count/file_size_bytes/dpi/uploaded_by/
            // uploaded_at (computed server-side -- see
            // src/services/artwork_file_metadata.py +
            // artwork_file_metadata_service.py). A genuinely-null field
            // still renders 'Unknown' rather than fabricating data -- this
            // is the graceful fallback, not a placeholder for missing
            // backend support.
            //
            // A stitch file (.dst/.emb) is a DESIGN, not a picture: its
            // pixel_width/pixel_height are the rendered preview PNG's
            // (meaningless) and it has no DPI. So it shows Design size
            // (measured_size_in, inches + mm) and Stitch count from the
            // preview drain's parse of the file, and no Dimensions or
            // Resolution row. Never the preview's pixels; Colors is
            // Unknown (colorsLabel) and File size is the stitch file's own,
            // sized by the backend at request time.
            const a = this.artwork || {}
            const rawName = a.original_filename || a.filename || ''
            const kind = artworkFileKind(rawName)
            const measureRows =
                kind === ARTWORK_FILE_KIND.STITCH
                    ? [
                        {
                            key: 'design-size',
                            label: 'Design size',
                            value: this.designSizeLabel(a.measured_size_in),
                        },
                        {
                            key: 'stitch-count',
                            label: 'Stitch count',
                            value: this.stitchCountLabel(a.stitch_count),
                        },
                    ]
                    : [
                        {
                            key: 'dimensions',
                            label: 'Dimensions',
                            value: this.dimensionsLabel(a, kind, rawName),
                        },
                        {
                            key: 'resolution',
                            label: 'Resolution',
                            value: this.resolutionLabel(a, kind, rawName),
                        },
                    ]
            return [
                { key: 'format', label: 'Format', value: this.formatOf(rawName) },
                ...measureRows,
                {
                    key: 'colors',
                    label: 'Colors',
                    value: this.colorsLabel(a, kind),
                },
                {
                    key: 'size',
                    label: 'File size',
                    value: this.fileSizeLabel(a.file_size_bytes),
                },
                {
                    key: 'uploaded-by',
                    label: 'Uploaded by',
                    value: a.uploaded_by || 'Unknown',
                },
                {
                    key: 'uploaded',
                    label: 'Uploaded',
                    value: this.uploadedDateLabel(a.uploaded_at),
                },
            ]
        },
        statusInfo() {
            // Mirrors the backend's ``_status_for`` truth table
            // (api/src/endpoints/artworks.py) so the header chip agrees
            // with the library list's derivation even though this
            // endpoint doesn't return a precomputed ``status`` field yet.
            const state = this.artwork && this.artwork.lifecycle_state
            if (state === 'archived') {
                return { key: 'archived', label: STATUS_LABELS.archived }
            }
            const productCount = this.whereUsed.rollup.product_count || 0
            if (productCount >= 1) {
                return { key: 'in-use', label: STATUS_LABELS['in-use'] }
            }
            // An uploaded stitch file (.dst/.emb) is its own production
            // file, so it is Ready even before any size exists
            // (``is_stitch_file`` is the server's own predicate).
            const anyDigitized = this.allDecorationCards.some(
                (c) => c.digitized,
            )
            const isStitchFile =
                !!this.artwork && this.artwork.is_stitch_file === true
            if (anyDigitized || isStitchFile) {
                return { key: 'ready', label: STATUS_LABELS.ready }
            }
            return { key: 'draft', label: STATUS_LABELS.draft }
        },
        allDecorationCards() {
            // ADR 0063 Locked Decision 15: one card per decoration -- the
            // method-tabbed grouping is gone. Flatten methods[].decorations
            // into a single list, stamping each row with its method label
            // and its ``digitized`` readiness flag. ``digitized`` comes from
            // the backend (artworks._is_digitized): a delivered digitizer
            // file OR an uploaded .dst/.emb, which is its own production
            // file. Deriving it here from digitization_output_url alone
            // said "Needs digitizing" for a stitch upload.
            const out = []
            for (const group of this.methods) {
                const methodLabel =
                    (group.method && group.method.display_name) || ''
                // Stamp the method slug too: stitch counts are an
                // embroidery-only concept, so stitchLabel() gates on it
                // (the per-artwork estimate would otherwise leak a
                // nonsensical "~8,000 stitches" onto screen-print/DTF
                // cards on an embroidery-bearing artwork).
                const methodSlug = (group.method && group.method.slug) || null
                for (const d of group.decorations) {
                    out.push({
                        ...d,
                        methodLabel,
                        methodSlug,
                        digitized: d.digitized === true,
                    })
                }
            }
            return out
        },
        manageReasonText() {
            const count = this.whereUsed.rollup.product_count || 0
            if (count > 0) {
                return (
                    `In use on ${count} product${count === 1 ? '' : 's'} -- ` +
                    'archive to hide it from pickers without affecting ' +
                    "live stores. Deleting is blocked while it's in use."
                )
            }
            return 'Not used anywhere, so it can be deleted outright.'
        },
        canDeleteArtwork() {
            return (this.whereUsed.rollup.product_count || 0) === 0
        },
        firstUsagePlacementId() {
            // The placement_id of the FIRST usage row in document order --
            // first product of the first group, matching the nested v-for
            // below.
            //
            // WHY A COMPUTED AND NOT A TEMPLATE INDEX. The rows are a nested
            // loop (groups, then products within a group), so `index === 0`
            // inside the inner v-for is true once PER STORE and would stamp
            // the anchor on several rows. Resolving it here computes "first
            // overall" once.
            //
            // WHY THE ANCHOR EXISTS AT ALL. `dla-whereused-dims-<placement_id>`
            // is suffixed with a database autoincrement, which is the right
            // anchor for a unit test (it controls the fixture) but unusable
            // from a ui-runner SCENARIO: a reseed renumbers it, so the
            // scenario captures the id from the DB and interpolates it.
            // Interpolation is wired into the step executors (GH #3194) but
            // NOT into `expect:` assertions -- _evaluate_dom_expectations and
            // _evaluate_text_expectations in tools/ui-runner/ui_runner/cli.py
            // receive the `expect` block without the captures map, so
            // `{{usage.placement_id}}` reaches document.querySelector
            // literally and dies as a CSS SyntaxError. R-1 (ui_runner/
            // schema.py) forbids the `^=` prefix workaround. A positional
            // anchor is stable under a reseed because it is a position, not
            // an identity.
            const groups = this.whereUsed.groups || []
            for (const group of groups) {
                const products = group.products || []
                if (products.length) {
                    const first = products[0]
                    return first == null ? null : first.placement_id
                }
            }
            return null
        },
        capabilities() {
            const inj = this.distributorLayoutCapabilities
            if (Array.isArray(inj)) return inj
            if (
                inj &&
                typeof inj === 'object' &&
                Array.isArray(inj.value)
            ) {
                return inj.value
            }
            return []
        },
        // Replace uploads, then calls /rebind, which enforces product.edit:
        // gate on both so a user never uploads and then gets a 403.
        canReplace() {
            return (
                this.canWrite && this.capabilities.includes('product.edit')
            )
        },
        canWrite() {
            return this.capabilities.includes(
                'distributor.decoration_library.write',
            )
        },
        canOverride() {
            // Approve a size on the client's behalf (the builder bypass).
            // The backend gates the same capability.
            return this.capabilities.includes(
                'distributor.decoration_library.override',
            )
        },
        overrideClients() {
            // The clients a size can be approved for on their behalf: the
            // ARTWORK's distributor's ACTIVE clients only (a lead or inactive
            // client cannot be the approver; a Super Admin's list spans
            // every tenant; the backend refuses any other), by name.
            const tenant = this.artwork?.distributor_id
            return this.clientRows
                .filter((c) => c.status === 'active')
                .filter(
                    (c) =>
                        tenant == null ||
                        String(c.distributor_id) === String(tenant),
                )
                .map((c) => ({ id: Number(c.id), name: c.name }))
                .sort((a, b) => String(a.name).localeCompare(String(b.name)))
        },
        deleteModalTitle() {
            if (this.deleteScope === 'artwork') return 'Delete artwork?'
            if (this.deleteScope === 'decoration')
                {return 'Delete decoration?'}
            return ''
        },
    },
    watch: {
        // Keep the field in step with the artwork (load, rename, replace).
        displayName: {
            immediate: true,
            handler(name) {
                this.nameDraft = name
            },
        },
        routeArtworkId: {
            immediate: true,
            handler(newId, oldId) {
                if (newId == null || newId === oldId) return
                this.fetchDecorations(newId)
                this.fetchWhereUsed(newId)
            },
        },
    },
    unmounted() {
        // Never let the artwork_preview poll outlive the component
        // (mirrors DecorationLibraryList.vue PR-6).
        this.stopPreviewPoll()
    },
    methods: {
        initialOf(name) {
            const first = String(name || '').trim().charAt(0)
            return first ? first.toUpperCase() : 'A'
        },
        formatOf(filename) {
            const m = /\.([a-zA-Z0-9]+)$/.exec(String(filename || ''))
            return m ? m[1].toUpperCase() : 'Unknown'
        },
        dimensionsLabel(artwork, kind, filename) {
            // A vector file has no pixel size of its own, and neither does a
            // PDF: the pixels stored for an embedded-raster PDF/AI are the
            // PAGE at the bitmap's density (a warning input), not
            // "Dimensions". The density is shown, labelled, in Resolution.
            if (kind === ARTWORK_FILE_KIND.VECTOR || this.isPdfName(filename)) {
                return 'Unknown'
            }
            const w = artwork.pixel_width
            const h = artwork.pixel_height
            if (w == null || h == null) return 'Unknown'
            return `${w} x ${h} px`
        },
        isPdfName(filename) {
            // PDF and PDF-compatible AI: the formats whose art can be an
            // embedded bitmap (the server stores that bitmap's density).
            return /\.(pdf|ai)$/i.test(String(filename || ''))
        },
        resolutionLabel(artwork, kind, filename) {
            // Resolution isn't a meaningful concept for a vector file, so
            // the file record says 'Vector' instead of 'Unknown'. A PDF or AI
            // whose art is an embedded bitmap carries that bitmap's density.
            if (this.isPdfName(filename) && artwork.dpi != null) {
                return `${artwork.dpi} DPI (embedded image)`
            }
            if (kind === ARTWORK_FILE_KIND.VECTOR) return 'Vector'
            if (artwork.dpi != null) return `${artwork.dpi} DPI`
            return 'Unknown'
        },
        designSizeLabel(size) {
            // A stitch file's physical size, e.g. "3.50 x 2.00 in (89 x
            // 51 mm)". Two decimals in inches, matching the Decorations
            // cards' sizes on this page; whole millimeters. Either side
            // missing or not a positive number -> Unknown.
            const w = Number(size && size.w)
            const h = Number(size && size.h)
            // Number(null) is 0 and Number(undefined) NaN: both fail here.
            const known = (n) => Number.isFinite(n) && n > 0
            if (!known(w) || !known(h)) return 'Unknown'
            const mm = (n) => Math.round(n * 25.4)
            return (
                `${w.toFixed(2)} x ${h.toFixed(2)} in ` +
                `(${mm(w)} x ${mm(h)} mm)`
            )
        },
        colorsLabel(artwork, kind) {
            // A stitch file's color_count is the rendered preview PNG's
            // distinct pixel count (2 for a 7-thread design), not a thread
            // count, and no thread count is stored -- so it is Unknown
            // rather than a wrong number (Jake, 2026-10-22).
            if (kind === ARTWORK_FILE_KIND.STITCH) return 'Unknown'
            return artwork.color_count != null
                ? String(artwork.color_count)
                : 'Unknown'
        },
        stitchCountLabel(count) {
            if (count == null) return 'Unknown'
            const n = Number(count)
            if (!Number.isFinite(n) || n < 0) return 'Unknown'
            return n.toLocaleString()
        },
        fileSizeLabel(bytes) {
            if (bytes == null) return 'Unknown'
            const n = Number(bytes)
            if (!Number.isFinite(n) || n < 0) return 'Unknown'
            if (n < 1024) return `${n} B`
            const kb = n / 1024
            if (kb < 1024) return `${kb.toFixed(kb < 10 ? 1 : 0)} KB`
            const mb = kb / 1024
            return `${mb.toFixed(mb < 10 ? 1 : 0)} MB`
        },
        uploadedDateLabel(isoString) {
            if (!isoString) return 'Unknown'
            const d = new Date(isoString)
            if (Number.isNaN(d.getTime())) return 'Unknown'
            return d.toLocaleDateString(undefined, {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
            })
        },
        dimsLabel(card) {
            const w =
                card.width_in != null
                    ? Number(card.width_in).toFixed(2)
                    : '?'
            const h =
                card.height_in != null
                    ? Number(card.height_in).toFixed(2)
                    : '?'
            return `${w}" x ${h}"`
        },
        placementsLabel(card) {
            const n = Number(card?.placements_count) || 0
            if (n === 1) return '1 product'
            return `${n} products`
        },
        stitchLabel(card) {
            // Decoration Library detail: stitch count actual-vs-estimate.
            // Actual (digitized) wins when present -- it is real, so no
            // qualifier. Otherwise fall back to the Wilcom/DST-parse
            // estimate, labeled so it's never mistaken for the real
            // count. Neither present (non-embroidery, or no data yet) ->
            // null, and the caller omits the line entirely.
            //
            // Stitch counts are embroidery-only. The backend assigns the
            // per-artwork estimate to EVERY decoration regardless of
            // method, so a screen-print/DTF card on an embroidery-bearing
            // artwork would otherwise render a nonsensical estimate. Gate
            // the whole line on the card's method slug.
            if (card?.methodSlug !== 'embroidery') return null
            const spec = card?.spec
            if (!spec) return null
            const actual = spec.actual_stitch_count
            if (actual != null) {
                return `${Number(actual).toLocaleString()} stitches`
            }
            const estimate = spec.stitch_count
            if (estimate != null && spec.stitch_count_final) {
                // An uploaded .dst/.emb: this IS the file's own count,
                // final for pricing (#4305) -- not an estimate.
                return `${Number(estimate).toLocaleString()} stitches`
            }
            if (estimate != null) {
                return `~${Number(estimate).toLocaleString()} stitches (estimated)`
            }
            return null
        },
        setBackdrop(key) {
            // Session state only -- never persisted, never sent to the
            // server (ADR 0063 Locked Decision 3).
            this.backdrop = key
        },
        async fetchDecorations(id) {
            // A route change targets a different artwork -- stop any poll
            // armed for the previous artwork before the fresh load.
            this.stopPreviewPoll()
            this.loading = true
            this.loadError = null
            this.notFound = false
            this.artwork = null
            this.methods = []
            // The preview picker unmounts while loading; whatever it was
            // showing belongs to the artwork we are replacing.
            this.colorwayPreview = null
            this.colorwayPreviewBusy = false
            try {
                const resp = await axios.get(
                    `/api/artworks/${id}/decorations`
                )
                const body = (resp && resp.data) || {}
                this.artwork = body.artwork || null
                this.methods = Array.isArray(body.methods)
                    ? body.methods
                    : []
                this.loadOverrideClients()
            } catch (err) {
                const status = err?.response?.status
                if (status === 404) {
                    this.notFound = true
                } else {
                    this.loadError = 'Could not load artwork.'
                }
            } finally {
                this.loading = false
            }
            // (Re)evaluate the lazy-render poll after every load. The
            // decorations endpoint itself is the server-side render
            // trigger (ensure_preview, artworks.py
            // get_artwork_decorations) -- the client just watches for the
            // sidecar to reach a terminal state.
            this.syncPreviewPoll()
        },

        async loadOverrideClients() {
            // Only an override holder sees the picker, so only it reads
            // the client list. The shared store's list is reused when
            // another page already loaded it.
            if (!this.canOverride) return
            const store = useClientsStore()
            if (!store.clients.length) await store.fetch()
            this.clientRows = Array.isArray(store.clients)
                ? store.clients
                : []
        },

        async refreshDecorations() {
            // Re-read the cards after an approval changed on one of them,
            // without the page-level spinner or clearing the header: the
            // server is the source of truth for every card's approval
            // state, so nothing is patched locally.
            const id = this.artwork?.id ?? this.routeArtworkId
            if (id == null) return
            try {
                const resp = await axios.get(
                    `/api/artworks/${id}/decorations`
                )
                const body = (resp && resp.data) || {}
                if (body.artwork) this.artwork = body.artwork
                if (Array.isArray(body.methods)) this.methods = body.methods
            } catch (err) {
                // Fall back to the full load, which owns the error states.
                await this.fetchDecorations(id)
            }
        },

        async fetchWhereUsed(id) {
            // ADR 0063 "Where it's used" -- U2's grouped-by-store
            // endpoint. Independent request from fetchDecorations() so a
            // slow/failed usage lookup never blocks the header + cards
            // from rendering.
            this.whereUsedLoading = true
            try {
                const resp = await axios.get(
                    `/api/artworks/${id}/where_used`
                )
                const body = (resp && resp.data) || {}
                this.whereUsed = {
                    groups: Array.isArray(body.groups) ? body.groups : [],
                    rollup: body.rollup || {
                        product_count: 0,
                        store_count: 0,
                        live_store_count: 0,
                    },
                }
            } catch (err) {
                // A failed usage lookup falls back to the real empty
                // state rather than blocking the page. The Manage
                // section's delete gate is conservative on a fetch
                // failure (product_count defaults to 0, i.e. "appears
                // deletable") but the server remains the actual
                // authority on delete eligibility regardless of what
                // this page shows -- confirmDelete()'s 409 handling
                // still protects an in-use artwork if this readout was
                // wrong.
                this.whereUsed = {
                    groups: [],
                    rollup: {
                        product_count: 0,
                        store_count: 0,
                        live_store_count: 0,
                    },
                }
            } finally {
                this.whereUsedLoading = false
            }
        },

        // ----- artwork_preview sidecar poll -----------------------------
        // Mirrors DecorationLibraryList.vue's syncPreviewPoll /
        // startPreviewPoll / stopPreviewPoll / pollPreviews (PR-6), scoped
        // to this single artwork instead of a list of rows.

        syncPreviewPoll() {
            if (isNonTerminalPreview(this.artwork && this.artwork.preview)) {
                this.startPreviewPoll()
            } else {
                this.stopPreviewPoll()
            }
        },

        startPreviewPoll() {
            if (this.previewPollTimer != null) return // already polling
            this.previewPollTimer = setInterval(() => {
                this.pollPreview()
            }, PREVIEW_POLL_INTERVAL_MS)
        },

        stopPreviewPoll() {
            if (this.previewPollTimer != null) {
                clearInterval(this.previewPollTimer)
                this.previewPollTimer = null
            }
        },

        async pollPreview() {
            // A silent re-fetch (no loading spinner, no error-banner
            // takeover) that re-hits the SAME decorations endpoint --
            // reusing the server-side ensure_preview trigger rather than
            // inventing a new poll endpoint. In-flight guard mirrors
            // DecorationLibraryList.vue PR-4b: at most one poll fetch
            // outstanding at a time, so a slow response can't race a
            // faster later tick and clobber this.artwork/methods with a
            // stale response.
            if (!this.artwork || this.previewPollInFlight) return
            const id = this.artwork.id
            this.previewPollInFlight = true
            try {
                const resp = await axios.get(
                    `/api/artworks/${id}/decorations`
                )
                const body = (resp && resp.data) || {}
                // Only apply the poll response if we're still looking at
                // the same artwork (a route change may have fired
                // fetchDecorations -- and its own stopPreviewPoll -- while
                // this GET was in flight).
                if (this.artwork && this.artwork.id === id) {
                    if (body.artwork) this.artwork = body.artwork
                    if (Array.isArray(body.methods)) {
                        this.methods = body.methods
                    }
                }
            } catch (err) {
                // A transient poll failure must not blow away the page or
                // stop the poll -- the next tick retries.
            } finally {
                this.previewPollInFlight = false
            }
            this.syncPreviewPoll()
        },

        goBack() {
            // Always send the user to the library list -- the path
            // matches the existing decoration_library_list named route.
            this.$router.push({ name: 'decoration_library_list' })
        },

        storeEditRoute(group) {
            // "Where it's used" store-group header -> that store's editor
            // (default section). Named route, not a hardcoded path, so a
            // future route-path change doesn't silently break this link.
            return {
                name: 'store_edit',
                params: { id: group.store_id },
            }
        },

        usageDimsLabel(product) {
            // Deliberately NOT named ``dimsLabel``: that name is already
            // taken by the per-decoration CARD formatter above, which pads
            // to two decimals. Reusing the name silently overrode it and
            // changed the cards from 4.00" x 3.00" to 4" x 3" -- caught by
            // DecorationLibraryArtworkDetail.spec.js, not by review.
            //
            // ``width_in`` / ``height_in`` are NOT NULL on the decorations
            // table (Numeric(5, 2)) and the endpoint float()s them, so a
            // row always has both. Trailing zeros are trimmed here -- 3.00
            // reads "3", 2.50 reads "2.5" -- because on a usage row the
            // stored scale is a column artifact, not information the
            // operator asked for. Guarded rather than assumed: a cached
            // payload predating the endpoint change carries neither key,
            // and ``undefined" x undefined"`` is worse than nothing.
            const w = product?.width_in
            const h = product?.height_in
            if (w == null || h == null) return ''
            return `${Number(w)}" x ${Number(h)}"`
        },

        productEditorRoute(product) {
            // "Where it's used" product row -> the product editor for
            // that store's specific product. ``product_id`` in the
            // where_used payload IS the campaign_product_id (the backend
            // join is on Placement.campaign_product_id -- see
            // api/src/endpoints/artworks.py get_artwork_where_used).
            // Deep-link to the exact placement when the payload carries
            // a placement_id; the current backend shape does not, so
            // this falls through to the plain product editor route.
            if (product.placement_id != null) {
                return {
                    name: 'product_editor_placement',
                    params: {
                        campaignProductId: product.product_id,
                        placementId: product.placement_id,
                    },
                }
            }
            return {
                name: 'product_editor',
                params: { campaignProductId: product.product_id },
            }
        },

        // Save the name when the field is left (native change event). A
        // blank or unchanged name sends nothing and puts the old name back.
        async saveName() {
            if (!this.artwork || this.nameSaving) return
            const next = (this.nameDraft || '').trim()
            if (!next || next === this.displayName) {
                this.nameDraft = this.displayName
                return
            }
            this.nameError = null
            this.nameSaving = true
            try {
                const resp = await axios.patch(
                    `/api/artworks/${this.artwork.id}/name`,
                    { name: next },
                )
                const saved = (resp && resp.data && resp.data.artwork) || {}
                this.artwork = {
                    ...this.artwork,
                    filename: saved.filename ?? this.artwork.filename,
                }
                this.nameDraft = this.displayName
            } catch (err) {
                // Keep the typed text so the user can correct it.
                const data = (err && err.response && err.response.data) || {}
                this.nameError =
                    data.error || 'Could not rename this artwork. Please try again.'
            } finally {
                this.nameSaving = false
            }
        },

        async archiveArtwork() {
            // ADR 0063 Locked Decision 18: Archive is always available
            // (unlike Delete, it is never gated on usage). Routes through
            // the existing lifecycle-state transition endpoint rather
            // than a new one.
            if (!this.artwork || this.archiveSubmitting) return
            this.archiveSubmitting = true
            this.archiveError = null
            try {
                const resp = await axios.post(
                    `/api/artworks/${this.artwork.id}/transition`,
                    { to_state: 'archived' },
                )
                const body = (resp && resp.data) || {}
                if (body.artwork) {
                    this.artwork = { ...this.artwork, ...body.artwork }
                }
            } catch (err) {
                const status = err?.response?.status
                const data = (err && err.response && err.response.data) || {}
                this.archiveError =
                    status === 409
                        ? data.error ||
                          'This artwork cannot be archived from its current state.'
                        : 'Could not archive this artwork. Please try again.'
            } finally {
                this.archiveSubmitting = false
            }
        },

        async downloadArtwork() {
            // Bug Intake #297. The API streams the ORIGINAL upload as an
            // attachment (tenant-checked); a cross-origin <a download> to
            // the bucket would be ignored by the browser.
            if (!this.artwork || this.downloadState === 'downloading') return
            this.downloadState = 'downloading'
            this.downloadError = null
            try {
                const resp = await axios.get(
                    `/api/artworks/${this.artwork.id}/download`,
                    { responseType: 'blob' },
                )
                const url = URL.createObjectURL(resp.data)
                const link = document.createElement('a')
                link.href = url
                link.download = this.downloadFilename(resp)
                document.body.appendChild(link)
                link.click()
                document.body.removeChild(link)
                URL.revokeObjectURL(url)
                this.downloadState = 'done'
            } catch (err) {
                this.downloadState = 'error'
                this.downloadError =
                    err?.response?.status === 404
                        ? 'The original file could not be found. Try re-uploading it.'
                        : 'Could not download this file. Please try again.'
            }
        },

        downloadFilename(resp) {
            // Prefer the server's name (the upload's own filename); fall
            // back to the display name the page already shows.
            const header = resp?.headers?.['content-disposition'] || ''
            return parseDispositionFilename(header) || this.displayName || 'artwork'
        },

        onArtworkReplaced({ artwork }) {
            // The replacement is a NEW library artwork that now carries the
            // old one's uses; go to it. (A replaced artwork that was used
            // nowhere comes here too: the new file is just in the library.)
            this.replaceOpen = false
            if (artwork && artwork.id != null) {
                this.$router.push({
                    name: 'library_artwork_detail',
                    params: { artwork_id: String(artwork.id) },
                })
            }
        },

        async openArtworkDeleteConfirm() {
            // Hand-off into the shared modal in 'artwork' scope.
            // Prefetch the cascade footprint so the modal copy is
            // concrete; on failure the soft error surfaces but the
            // confirm button still works (server is the authority).
            if (!this.artwork || !this.canDeleteArtwork) return
            this.deleteScope = 'artwork'
            this.decorationDeleteCandidate = null
            this.deleteError = null
            this.deletePreviewSummary = {
                decorations: 0,
                placements: 0,
                campaign_products: 0,
                in_flight_orders: 0,
            }
            this.deletePreviewLoading = true
            try {
                const resp = await axios.get(
                    `/api/artworks/${this.artwork.id}/delete-preview/`,
                )
                this.deletePreviewSummary =
                    (resp && resp.data) || this.deletePreviewSummary
            } catch (err) {
                this.deleteError = 'Could not load delete preview'
            } finally {
                this.deletePreviewLoading = false
            }
        },

        openDecorationDeleteConfirm(card) {
            // Decoration scope -- no preview round-trip; the
            // per-card placements_count badge is enough to render
            // the warning copy.
            this.deleteScope = 'decoration'
            this.decorationDeleteCandidate = card
            this.deleteError = null
            this.deletePreviewSummary = {
                decorations: 0,
                placements:
                    (card && Number(card.placements_count)) || 0,
                campaign_products: 0,
                in_flight_orders: 0,
            }
            this.deletePreviewLoading = false
        },

        closeDeleteConfirm() {
            this.deleteScope = null
            this.decorationDeleteCandidate = null
            this.deleteError = null
            this.deleteSubmitting = false
        },

        async confirmDelete() {
            if (!this.deleteScope || this.deleteSubmitting) return
            this.deleteSubmitting = true
            this.deleteError = null
            const scope = this.deleteScope
            try {
                if (scope === 'artwork') {
                    await axios.post(
                        `/api/artworks/${this.artwork.id}/cascade-delete/`,
                    )
                    // Navigate back to the library list -- the
                    // artwork is gone server-side.
                    this.deleteScope = null
                    this.$router.push({
                        name: 'decoration_library_list',
                    })
                } else if (scope === 'decoration') {
                    const card = this.decorationDeleteCandidate
                    if (!card) {
                        this.deleteScope = null
                        return
                    }
                    await axios.post(
                        `/api/decorations/${card.id}/cascade-delete/`,
                    )
                    // Remove the card from its method group in-place.
                    this.removeDecorationFromMethods(card.id)
                    this.deleteScope = null
                }
            } catch (err) {
                const status =
                    err && err.response && err.response.status
                const data =
                    (err && err.response && err.response.data) || {}
                if (
                    status === 409 &&
                    data.error === 'in_flight_orders'
                ) {
                    this.deleteError =
                        `Cannot delete: ${data.count || 0} in-flight ` +
                        `order(s) reference this. Wait for the orders ` +
                        `to ship or cancel them, then try again.`
                } else if (status === 404) {
                    // Already deleted server-side. Close modal.
                    if (scope === 'artwork') {
                        this.deleteScope = null
                        this.$router.push({
                            name: 'decoration_library_list',
                        })
                    } else {
                        const card = this.decorationDeleteCandidate
                        if (card) this.removeDecorationFromMethods(card.id)
                        this.deleteScope = null
                    }
                } else {
                    this.deleteError = 'Delete failed. Please try again.'
                }
            } finally {
                this.deleteSubmitting = false
            }
        },

        removeDecorationFromMethods(decorationId) {
            // Filter the deleted Decoration out of its method group. If
            // the method group ends up empty, drop it so the UI doesn't
            // carry a stranded empty group (harmless now that groups are
            // flattened for display, but keeps the underlying ``methods``
            // shape consistent with what the endpoint would return on a
            // fresh load).
            const next = []
            for (const group of this.methods) {
                const remaining = group.decorations.filter(
                    (d) => d.id !== decorationId,
                )
                if (remaining.length) {
                    next.push({ ...group, decorations: remaining })
                }
            }
            this.methods = next
        },
    },
}
</script>

<style scoped>
.dla-container {
    background: var(--tf-surface-light, #f5f8fa);
    min-height: 100vh;
}
.dla {
    max-width: 1240px;
    margin: 0 auto;
    padding: 16px 24px 64px;
}
.dla__backlink {
    appearance: none;
    background: none;
    border: none;
    color: var(--tf-primary, #119ae9);
    cursor: pointer;
    padding: 8px 0;
    font-size: 14px;
}
.dla__loading,
.dla__error,
.dla__not-found {
    padding: 48px 24px;
    text-align: center;
    background: #ffffff;
    border-radius: var(--tf-radius-md, 12px);
    margin-top: 24px;
}
.dla__not-found-title {
    font-size: 22px;
    font-weight: 600;
    color: var(--tf-secondary, #133752);
    margin-bottom: 8px;
}
.dla__not-found-sub {
    color: var(--tf-fg-4, #757575);
    margin-bottom: 16px;
}
.dla__not-found-back {
    appearance: none;
    background: var(--tf-secondary, #133752);
    color: #ffffff;
    border: none;
    border-radius: var(--tf-radius-pill, 9999px);
    padding: 8px 16px;
    cursor: pointer;
}
.dla__error {
    color: #b91c1c;
    background: #fef2f2;
}

/* ------------------------------------------------------------------ */
/* Two-column split (ADR 0063 Locked Decision 13). Flexible tracks use */
/* minmax(0,1fr) so the layout never overflows at narrow widths.       */
/* ------------------------------------------------------------------ */
.dla__split {
    display: grid;
    grid-template-columns: minmax(260px, 344px) minmax(0, 1fr);
    gap: 28px;
    align-items: start;
    margin-top: 20px;
}
@media (max-width: 1039px) {
    .dla__split {
        grid-template-columns: minmax(0, 1fr);
    }
    .dla__rail {
        position: static !important;
    }
}

.dla__rail {
    position: sticky;
    top: 16px;
    min-width: 0;
}
.dla__rail-card {
    background: #ffffff;
    border: 1px solid var(--tf-border, #e0e0e0);
    border-radius: var(--tf-radius-md, 12px);
    overflow: hidden;
}
.dla__preview-frame {
    position: relative;
    width: 100%;
    aspect-ratio: 1 / 1;
    max-height: 272px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 12%;
    box-sizing: border-box;
    border-bottom: 1px solid var(--tf-border, #e0e0e0);
}
.dla__preview-img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
    display: block;
}
.dla__thumb-placeholder {
    width: 96px;
    height: 96px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 28px;
    color: #6b7280;
    background: #e5e7eb;
}
.dla__thumb-pending,
.dla__thumb-failed {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 4px;
    text-align: center;
}
.dla__thumb-pending-text {
    font-size: 12px;
    line-height: 1.3;
    color: rgba(0, 0, 0, 0.6);
}
.dla__thumb-failed-text {
    font-size: 12px;
    line-height: 1.3;
    color: #8a1c1c;
}
.dla__thumb-no-preview-hint {
    font-size: 11px;
    color: rgba(0, 0, 0, 0.5);
}
.dla__thumb-no-preview {
    display: flex;
    flex-direction: column;
    gap: 4px;
    align-items: center;
    justify-content: center;
    text-align: center;
    padding: 8px;
    font-size: 12px;
    line-height: 1.3;
    color: rgba(0, 0, 0, 0.6);
}
.dla__backdrop-row {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 14px;
    border-bottom: 1px solid var(--tf-border, #e0e0e0);
}
.dla__backdrop-label {
    font-size: 11.5px;
    color: var(--tf-fg-4, #757575);
    flex: 1;
}
.dla__backdrop-switcher {
    display: flex;
    gap: 4px;
    background: var(--tf-surface-alt, #eceff1);
    border-radius: 8px;
    padding: 3px;
}
.dla-backdrop-option {
    appearance: none;
    border: none;
    cursor: pointer;
    border-radius: 6px;
    padding: 5px 9px;
    background: transparent;
    color: var(--tf-fg-4, #757575);
    font-size: 11px;
}
.dla-backdrop-option--active {
    background: #fff;
    color: var(--tf-fg-1, #212121);
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.12);
}
.dla__colorways-row {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px 12px;
    margin: 10px 0 2px;
}
.dla__colorways-select {
    flex: 1 1 180px;
    min-width: 0;
    margin: 0 !important;
}
.dla__colorway-busy {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(255, 255, 255, 0.55);
}
.dla__colorways-link {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    margin: 0;
    font-size: 0.8125rem;
    color: var(--tf-primary, #119ae9);
    text-decoration: none;
}
.dla__colorways-link:hover {
    text-decoration: underline;
}
.dla__file-record {
    padding: 12px 16px 14px;
}
.dla__file-record-row {
    display: flex;
    justify-content: space-between;
    gap: 16px;
    padding: 7px 0;
    border-bottom: 1px solid var(--tf-border, #e0e0e0);
}
.dla__file-record-row:last-child {
    border-bottom: none;
}
.dla__file-record-k {
    font-size: 12.5px;
    color: var(--tf-fg-4, #757575);
}
.dla__file-record-v {
    font-size: 12.5px;
    color: var(--tf-fg-1, #212121);
    font-family: var(--tf-font-mono, monospace);
    text-align: right;
}

/* ------------------------------------------------------------------ */
/* Right working column                                                */
/* ------------------------------------------------------------------ */
.dla__working-column {
    min-width: 0;
}
.dla__header {
    display: flex;
    align-items: flex-start;
    gap: 16px;
    flex-wrap: wrap;
    margin-bottom: 22px;
}
.dla__header-main {
    flex: 1 1 320px;
    min-width: 0;
}
.dla__header-title-row {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
    margin-bottom: 5px;
}
.dla__header-title {
    font-size: 26px;
    font-weight: 500;
    color: var(--tf-secondary, #133752);
    font-family: var(--tf-font-display, sans-serif);
    letter-spacing: -0.3px;
    margin: 0;
}
.dla__header-file {
    font-size: 13px;
    color: var(--tf-fg-3, #616161);
    overflow-wrap: anywhere;
}
.dla__name-field {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 6px;
}
.dla__name-label {
    font-size: 13px;
    color: var(--tf-fg-3, #616161);
}
.dla__name-input {
    min-width: 260px;
    padding: 5px 8px;
    font-size: 14px;
    border: 1px solid var(--tf-border, #c9c9c9);
    border-radius: 6px;
    background: #fff;
}
.dla__header-client {
    font-size: 13px;
    color: var(--tf-fg-3, #616161);
}
.dla__status-chip {
    display: inline-flex;
    align-items: center;
    font-size: 11.5px;
    font-weight: 500;
    padding: 3px 9px;
    border-radius: var(--tf-radius-pill, 9999px);
    white-space: nowrap;
}
.dla__status-chip--draft {
    background: #eef0f3;
    color: var(--tf-fg-4, #757575);
}
.dla__status-chip--ready {
    background: #eaf4fc;
    color: #0b6fa4;
}
.dla__status-chip--in-use {
    background: #e6f7ed;
    color: #12805a;
}
/* "Archived" is a STATUS the user reads, not a disabled control -- its
   sibling --in-use paints a saturated green, so this chip's label is
   meaningful text and belongs on the readable scale, not the disabled grey. */
.dla__status-chip--archived {
    background: #f5f5f5;
    color: var(--tf-fg-4, #636363);
}
.dla__header-actions {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
}
.dla__header-error {
    color: #d32f2f;
    font-size: 13px;
    margin: 8px 0 0;
}

.dla__section {
    margin-bottom: 26px;
}
.dla__section-head {
    display: flex;
    align-items: baseline;
    gap: 9px;
    margin-bottom: 11px;
}
.dla__section-title {
    font-size: 16px;
    font-weight: 500;
    color: var(--tf-fg-1, #212121);
    font-family: var(--tf-font-display, sans-serif);
    margin: 0;
}
.dla__section-count {
    font-size: 12.5px;
    color: var(--tf-fg-4);
}

.dla__empty {
    background: #fbfcfd;
    border: 1px dashed var(--tf-border-strong, #d6d6d6);
    border-radius: var(--tf-radius-md, 12px);
    padding: 32px 24px;
    text-align: center;
}
.dla__empty-text {
    color: var(--tf-fg-4, #757575);
    margin: 0;
}

.dla__cards {
    display: flex;
    flex-direction: column;
    gap: 10px;
}
.dla__card {
    background: #ffffff;
    border: 1px solid var(--tf-border, #e0e0e0);
    border-radius: 10px;
    padding: 14px 16px;
    display: flex;
    flex-direction: column;
    gap: 10px;
}
.dla__card-head {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
}
.dla__method-chip {
    display: inline-flex;
    align-items: center;
    background: #ede7f6;
    color: #5e35b1;
    font-size: 11.5px;
    font-weight: 500;
    padding: 3px 9px;
    border-radius: var(--tf-radius-pill, 9999px);
}
.dla__card-dims {
    font-size: 16px;
    font-weight: 500;
    color: var(--tf-fg-1, #212121);
    font-family: var(--tf-font-mono, monospace);
}
.dla__card-meta {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
    gap: 8px 20px;
}
.dla__card-meta-k {
    font-size: 11px;
    color: var(--tf-fg-3);
    letter-spacing: 0.6px;
    text-transform: uppercase;
    margin-bottom: 2px;
}
.dla__card-meta-v {
    font-size: 13px;
    color: var(--tf-fg-2, #424242);
}
.dla__card-stitch {
    margin-top: 2px;
}
.dla__card-actions {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    border-top: 1px dashed var(--tf-border, #e0e0e0);
    padding-top: 8px;
}

/* Where it's used. */
.dla__where-used-loading,
.dla__where-used-empty {
    border: 1px dashed var(--tf-border-strong, #d6d6d6);
    border-radius: 10px;
    padding: 28px 22px;
    text-align: center;
    background: #fbfcfd;
}
.dla__where-used-empty-text {
    font-size: 13px;
    color: var(--tf-fg-3, #616161);
    margin: 0 0 14px;
    max-width: 480px;
    margin-left: auto;
    margin-right: auto;
}
.dla__where-used {
    display: flex;
    flex-direction: column;
    gap: 12px;
}
.dla__where-used-rollup {
    font-size: 13px;
    color: var(--tf-fg-3, #616161);
}
.dla__store-group {
    border: 1px solid var(--tf-border, #e0e0e0);
    border-radius: 10px;
    overflow: hidden;
    background: #ffffff;
}
.dla__store-group-head {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 14px;
    background: #fbfcfd;
    border-bottom: 1px solid var(--tf-border, #e0e0e0);
}
.dla__store-group-name {
    font-size: 13.5px;
    font-weight: 500;
    color: var(--tf-fg-1, #212121);
    flex: 1;
}
.dla__store-group-name--link {
    text-decoration: none;
    cursor: pointer;
}
.dla__store-group-name--link:hover {
    color: var(--tf-primary, #119ae9);
    text-decoration: underline;
}
.dla__store-status-chip {
    display: inline-flex;
    align-items: center;
    font-size: 11px;
    font-weight: 500;
    padding: 2px 8px;
    border-radius: var(--tf-radius-pill, 9999px);
}
.dla__store-status-chip--live {
    background: #e6f7ed;
    color: #12805a;
}
.dla__store-status-chip--draft {
    background: #eef0f3;
    color: var(--tf-fg-4, #757575);
}
.dla__store-product-row {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 9px 14px;
    border-top: 1px solid var(--tf-border, #e0e0e0);
    font-size: 13px;
}
.dla__store-product-row--link {
    text-decoration: none;
    cursor: pointer;
}
.dla__store-product-row--link:hover {
    background: rgba(17, 154, 233, 0.06);
}
.dla__store-product-row--link:hover .dla__store-product-name {
    color: var(--tf-primary, #119ae9);
    text-decoration: underline;
}
.dla__store-product-name {
    flex: 1;
    color: var(--tf-fg-1, #212121);
}
.dla__store-product-zone {
    color: var(--tf-fg-3, #616161);
    font-size: 12.5px;
    width: 130px;
}
.dla__store-product-method {
    color: var(--tf-fg-4, #757575);
    font-size: 12.5px;
    width: 110px;
    text-align: right;
}
/* Size of the usage. Sits after the method as the last column on the row;
   tabular-nums so the sizes line up down the column and a taller/wider
   pair is scannable at a glance rather than needing to be read. */
.dla__store-product-dims {
    color: var(--tf-fg-4, #757575);
    font-size: 12.5px;
    width: 96px;
    text-align: right;
    font-variant-numeric: tabular-nums;
}
/* The scenario anchor carries the same text as the visible dims span on the
   first usage row, so it must not paint -- otherwise the row shows its size
   twice. `display: none` rather than a clip/offscreen technique because the
   element is not for screen readers either (it is aria-hidden): nothing
   should perceive it except a test probe, and probe_text reads textContent,
   which display:none does not strip. Overrides the width/text-align above
   via the extra class specificity. */
.dla__store-product-dims--anchor {
    display: none;
}
/* The "Where it's used" product thumbnail + hover-zoom now lives in the
   shared <ProductThumbHoverZoom> component (frontend/src/components/common).
   Its root is a flex child of .dla__store-product-row and carries its own
   sizing/frame/overlay styles (scoped). */

/* Manage. */
.dla__manage-row {
    border: 1px solid var(--tf-border, #e0e0e0);
    border-radius: 10px;
    padding: 14px 16px;
    display: flex;
    align-items: center;
    gap: 16px;
    background: #ffffff;
    flex-wrap: wrap;
}
.dla__manage-copy {
    flex: 1;
    min-width: 200px;
}
.dla__manage-title {
    font-size: 13.5px;
    color: var(--tf-fg-1, #212121);
    margin-bottom: 2px;
}
.dla__manage-reason {
    font-size: 12.5px;
    color: var(--tf-fg-4, #757575);
}
.dla__manage-error {
    font-size: 12.5px;
    color: #b00020;
    margin-top: 4px;
}

/* Buttons. */
.dla__btn {
    appearance: none;
    background: #ffffff;
    border: 1px solid var(--tf-border, #e0e0e0);
    border-radius: var(--tf-radius-pill, 9999px);
    padding: 7px 14px;
    font-size: 13px;
    cursor: pointer;
    color: var(--tf-fg-2, #424242);
    text-decoration: none;
    display: inline-flex;
    align-items: center;
}
.dla__btn--sm {
    padding: 5px 11px;
    font-size: 12px;
}
.dla__btn:hover:not(:disabled) {
    background: rgba(0, 0, 0, 0.04);
}
.dla__btn:disabled {
    color: rgba(0, 0, 0, 0.35);
    cursor: not-allowed;
}
.dla__btn--primary {
    background: var(--tf-primary, #119ae9);
    border-color: var(--tf-primary, #119ae9);
    color: #ffffff;
    font-weight: 500;
}
.dla__btn--primary:hover:not(:disabled) {
    background: var(--tf-primary-hover, #0f8cd4);
}
.dla__btn--danger {
    color: #b00020;
    border-color: #b00020;
}
.dla__btn--danger:hover:not(:disabled) {
    background: #b00020;
    color: #fff;
}
.dla__btn--danger:disabled {
    color: rgba(176, 0, 32, 0.4);
    border-color: rgba(176, 0, 32, 0.25);
}

.dla__modal-backdrop {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.4);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1200;
}

.dla__modal {
    background: #fff;
    border-radius: 6px;
    padding: 24px;
    width: min(560px, 92vw);
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.25);
}

.dla__modal-title {
    font-size: 18px;
    font-weight: 600;
    margin: 0 0 12px 0;
    color: var(--tf-secondary, #133752);
}

.dla__modal-body {
    font-size: 14px;
    color: rgba(0, 0, 0, 0.8);
    margin: 0 0 12px 0;
}

.dla__modal-warn {
    font-size: 13px;
    color: #92400e;
    background: #fef3c7;
    border-radius: 4px;
    padding: 8px 12px;
    margin: 0 0 12px 0;
}

.dla__modal-error {
    font-size: 13px;
    color: #b00020;
    background: #fef2f2;
    border-radius: 4px;
    padding: 8px 12px;
    margin: 0 0 12px 0;
}

.dla__modal-actions {
    display: flex;
    gap: 8px;
    justify-content: flex-end;
}
</style>
